import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Search, Filter, Activity, AlertTriangle, Eye } from 'lucide-react';

export default function AuditPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [security, setSecurity] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('logs');

  useEffect(() => {
    api.get('/audit/logs?limit=200').then(r => setLogs(r.data)).catch(() => {});
    api.get('/security/activity-summary').then(r => setSecurity(r.data)).catch(() => {});
  }, [api]);

  const filtered = logs.filter(l => {
    if (!search) return true;
    return l.user_email?.includes(search) || l.action?.includes(search) || l.resource?.includes(search);
  });

  const ACTION_COLORS = {
    login: '#3b82f6', register: '#8b5cf6', create: '#10b981', update: '#f59e0b',
    review: '#06b6d4', payment_received: '#10b981', withdrawal: '#ef4444'
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Security & Audit</h1>
          <p className="text-xs text-zinc-500 mt-1">System activity logs and security monitoring</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-0">
        {[{k:'logs', l:'Audit Logs'}, {k:'security', l:'Security Sentinel'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${tab === t.k ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'logs' && (
        <>
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input data-testid="audit-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search logs..." className="w-full h-8 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <table className="w-full" data-testid="audit-table">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 font-medium">Time</th>
                  <th className="text-left px-4 py-2.5 font-medium">User</th>
                  <th className="text-left px-4 py-2.5 font-medium">Action</th>
                  <th className="text-left px-4 py-2.5 font-medium">Resource</th>
                  <th className="text-left px-4 py-2.5 font-medium">Details</th>
                  <th className="text-left px-4 py-2.5 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/30">
                {filtered.slice(0, 50).map(log => (
                  <tr key={log.id} className="hover:bg-zinc-800/20 transition-colors">
                    <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</td>
                    <td className="px-4 py-2 text-xs text-zinc-300">{log.user_email}</td>
                    <td className="px-4 py-2">
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: `${ACTION_COLORS[log.action] || '#71717a'}20`, color: ACTION_COLORS[log.action] || '#a1a1aa' }}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[10px] text-zinc-500">{log.resource}</td>
                    <td className="px-4 py-2 text-[10px] text-zinc-600 max-w-xs truncate">{log.details}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-zinc-600">{log.ip_address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'security' && security && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-blue-400" /><span className="text-[10px] text-zinc-500 uppercase">Total Events</span></div>
              <p className="text-2xl font-heading font-bold text-white">{security.total_audit_events}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-[10px] text-zinc-500 uppercase">Active Alerts</span></div>
              <p className="text-2xl font-heading font-bold text-amber-400">{security.alerts?.length || 0}</p>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Most Active Users</h3></div>
            <div className="divide-y divide-zinc-800/30">
              {security.top_active_users?.map((u, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs text-zinc-300">{u.email}</span>
                  <span className="text-xs font-mono text-zinc-500">{u.actions} actions</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Recent Logins</h3></div>
            <div className="divide-y divide-zinc-800/30">
              {security.recent_logins?.slice(0, 10).map((l, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-300">{l.user_email}</p>
                    <p className="text-[10px] text-zinc-600">IP: {l.ip_address || 'N/A'}</p>
                  </div>
                  <span className="text-[10px] font-mono text-zinc-500">{l.timestamp ? new Date(l.timestamp).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
